import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { getApiBaseUrl } from '@/config/env';
import { STORAGE_KEYS } from '@/constants/storage';
import type { ApiResponse } from '@/types/api';
import type { LoginResponse } from '@/types/auth';
import { ApiError } from '@/utils/apiError';
import {
  dispatchModuleAccessDenied,
  isModuleNotEnabledMessage,
  parseModuleFromAccessMessage,
} from '@/utils/moduleNavigation';
import { getModuleLoginPath } from '@/config/moduleEntries';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { readEntryModuleFromToken, readStoredEntryModule, clearEntryModuleStorage } from '@/utils/moduleScope';

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function resolveLoginUrl(): string {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const entryModule = readEntryModuleFromToken(token) ?? readStoredEntryModule();
  if (entryModule) {
    return getModuleLoginPath(entryModule);
  }
  return MODULE_HUB_PATH;
}

function processQueue(error: unknown, token: string | null) {
  refreshQueue.forEach((p) => {
    if (error) p.reject(error);
    else if (token) p.resolve(token);
  });
  refreshQueue = [];
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && original && !original._retry) {
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!refreshToken) {
        clearAuthStorage();
        window.location.href = resolveLoginUrl();
        return Promise.reject(ApiError.fromUnknown(error));
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (token: string) => {
              if (original.headers) original.headers.Authorization = `Bearer ${token}`;
              resolve(api(original));
            },
            reject,
          });
        });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post<ApiResponse<LoginResponse>>(
          `${getApiBaseUrl()}/v1/auth/refresh`,
          { refreshToken },
        );
        if (!data.success || !data.data) throw new Error('Refresh failed');
        const { accessToken, refreshToken: newRefresh } = data.data;
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefresh);
        processQueue(null, accessToken);
        if (original.headers) original.headers.Authorization = `Bearer ${accessToken}`;
        return api(original);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuthStorage();
        window.location.href = resolveLoginUrl();
        return Promise.reject(ApiError.fromUnknown(refreshError));
      } finally {
        isRefreshing = false;
      }
    }

    const apiMessage =
      typeof error.response?.data?.message === 'string'
        ? error.response.data.message
        : typeof error.response?.data?.error === 'string'
          ? error.response.data.error
          : undefined;
    if (error.response?.status === 403 && isModuleNotEnabledMessage(apiMessage)) {
      dispatchModuleAccessDenied({
        message: apiMessage ?? 'Module is not enabled for this organization',
        module: apiMessage ? parseModuleFromAccessMessage(apiMessage) : null,
      });
    }

    return Promise.reject(ApiError.fromUnknown(error));
  },
);

export function clearAuthStorage() {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  clearEntryModuleStorage();
}

export function setAuthTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
}

export async function unwrap<T>(promise: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  try {
    const { data } = await promise;
    if (!data.success) {
      throw ApiError.fromResponse(data);
    }
    return data.data as T;
  } catch (err) {
    throw ApiError.fromUnknown(err);
  }
}

export default api;
